import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { lazy, Suspense, useEffect, useState } from 'react';
import { SiteShell } from './SiteShell';
import { useAuth, silentRefreshOnBoot, UserType } from '@shared/lib/auth/useAuth';
import { AlertDialogHost } from '@shared/ui/AlertDialog';
import { DevIdInspector } from '@shared/lib/dev/DevIdInspector';
import {
  FeedbackLauncher,
  FeedbackHighlighter,
  FeedbackChatPage,
  isDevFeedbackEnabled,
  setDevFeedbackTokenProvider,
} from '@shared/thirdparty/dev-feedback';
import { getToken } from '@shared/api/httpClient';

// dev-feedback: 제출은 비로그인도 가능, 리스트/상세/첨부는 관리자(기관 담당자) 토큰이 있을 때만 열림.
setDevFeedbackTokenProvider(getToken);

// 개발 도구 게이트 — 빌드 시점 상수를 앞에 둬 prod 빌드(`--mode prod`)에서는 `false &&` 로 접혀
// 런처·인스펙터·모바일 피드백 페이지 코드가 번들에서 제거된다 (런타임 isDevFeedbackEnabled 만으로는 코드가 남음).
// dev 빌드는 VITE_APP_ENV=dev 로 런타임 게이트가 true.
const DEV_TOOLS_ENABLED = import.meta.env.MODE !== 'prod' && isDevFeedbackEnabled();

const HomePage       = lazy(() => import('@pages/home/desktop/HomePage'));
const LoginPage      = lazy(() => import('@pages/login/desktop/LoginPage'));
const SignupPage     = lazy(() => import('@pages/signup/desktop/SignupPage'));
const QuickTestPage  = lazy(() => import('@pages/quick-test/desktop/QuickTestPage'));
const RoundList      = lazy(() => import('@pages/round/desktop/RoundListPage'));
const RoundIntro     = lazy(() => import('@pages/round/desktop/RoundIntroPage'));
const SubmissionInfo = lazy(() => import('@pages/round/desktop/SubmissionInfoPage'));
const SurveyRespond  = lazy(() => import('@pages/round/desktop/SurveyRespondPage'));
const Result         = lazy(() => import('@pages/round/desktop/ResultPage'));
const MemberAnswerSheet = lazy(() => import('@pages/round/desktop/MemberAnswerSheetPage'));
const MyPage         = lazy(() => import('@pages/mypage/desktop/MyPageHistory'));
const GuidePage      = lazy(() => import('@pages/guide/GuidePage'));
const IntroPage      = lazy(() => import('@pages/intro/IntroPage'));
const AboutPage      = lazy(() => import('@pages/about/AboutPage'));
const ResearchPage   = lazy(() => import('@pages/research/ResearchPage'));
const AgencyParticipantList = lazy(() => import('@pages/agency/ParticipantListPage'));
const AgencyJaRespond       = lazy(() => import('@pages/agency/JaRespondPage'));
const AgencyVoucher         = lazy(() => import('@pages/agency/voucher/VoucherPage'));
const AgencyMyPage          = lazy(() => import('@pages/agency/mypage/AgencyMyPage'));
const AgencyMemberReport    = lazy(() => import('@pages/agency/member-report/AgencyMemberReportPage'));
const AgencyMemberMyPage    = lazy(() => import('@pages/agency/member-page/AgencyMemberMyPage'));
const ResultPdfExport       = lazy(() => import('@pages/result/desktop/ResultPdfExport'));
const RaterLogin            = lazy(() => import('@pages/rater/desktop/RaterLoginPage'));
const RaterAssignments      = lazy(() => import('@pages/rater/desktop/AssignmentListPage'));
const RaterAssessment       = lazy(() => import('@pages/rater/desktop/AssessmentDetailPage'));
const RaterEvalSheet        = lazy(() => import('@pages/rater/desktop/RaterEvalSheetPage'));
const TermsPage             = lazy(() => import('@pages/legal/TermsPage'));
const PrivacyPage           = lazy(() => import('@pages/legal/PrivacyPage'));
const HealthConsentPage     = lazy(() => import('@pages/legal/HealthConsentPage'));

interface RequireAuthProps {
  children: JSX.Element;
  userTypes?: UserType[]; // 지정 시 해당 타입만 허용
}

// 로그인 여부만 확인 — 역할별 뷰 분기는 userTypes 로 제한.
// 예외: URL 에 ?adminToken=... 가 있으면 관리자/평가자/응답자가 서로의 원본
//       설문지(SurveyRespondPage) · 평가지(RaterAssessmentDetailPage)를
//       read-only 로 공통 열람하는 흐름이므로 role 가드를 통과시킨다.
//       (백엔드 JWT secret 동일 → 토큰을 그대로 인증에 사용)
function hasAdminTokenParam(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).has('adminToken');
}
// 응답자는 결과지만 본다 — 예전 평가지 주소는 결과지로 이동 (평가지는 평가자 화면 /rater/assessment)
function EvalSheetToResult() {
  const { id } = useParams<{ id: string }>();
  const { search } = useLocation();
  return <Navigate to={`/round/${id}/result${search}`} replace />;
}

function RequireAuth({ children, userTypes }: RequireAuthProps) {
  const member = useAuth((s) => s.member);
  if (!member) {
    if (hasAdminTokenParam()) return children;
    return <Navigate to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }
  if (userTypes && !userTypes.includes(member.userType)) {
    if (hasAdminTokenParam()) return children;
    const fallback =
      member.userType === 'MANAGER' ? '/agency/participant' :
      member.userType === 'RATER'   ? '/rater' :
                                      '/round';
    return <Navigate to={fallback} replace />;
  }
  return children;
}

export default function App() {
  const [booted, setBooted] = useState(false);

  useEffect(() => {
    silentRefreshOnBoot().finally(() => setBooted(true));
  }, []);

  if (!booted) return null;

  return (
    <Suspense fallback={null}>
      {/* local/dev 전용 개발 도구 — 요소 id 인스펙터 + 피드백 런처 [손잡이|🦊|+] (navido 캐릭터 여우주). prod 는 isDevFeedbackEnabled()=false 로 미마운트. */}
      {DEV_TOOLS_ENABLED && <DevIdInspector />}
      {DEV_TOOLS_ENABLED && <FeedbackLauncher appCode="navido-service" icon="🦊" />}
      {/* 피드백 '해당 화면에서 보기' — 주소의 fbhl 파라미터로 지정 요소 박스 표시 (2026-10-06) */}
      {DEV_TOOLS_ENABLED && <FeedbackHighlighter />}
      <Routes>
        {/* 메인 헤더 화면 — 헤더를 공통 틀에 고정 (화면 이동 중 헤더 깜박임 X · 2026-11-01 mid=470) */}
        <Route element={<SiteShell />}>
          {/* 첫 화면 = 인트로 · 검사 카드 메인 = /main (2026-10-21 · NAVIDO-3 결정 ①) · 상단 메뉴 페이지 (R06-01) */}
          <Route path="/"                       element={<IntroPage />} />
          <Route path="/main"                   element={<HomePage />} />
          <Route path="/guide"                  element={<GuidePage />} />
          <Route path="/about"                  element={<AboutPage />} />
          <Route path="/research"               element={<ResearchPage />} />
          <Route path="/round"                  element={<RequireAuth><RoundList /></RequireAuth>} />
          <Route path="/round/:id"              element={<RequireAuth><RoundIntro /></RequireAuth>} />
          <Route path="/round/:id/result"       element={<RequireAuth><Result /></RequireAuth>} />
          <Route path="/mypage"                 element={<RequireAuth><MyPage /></RequireAuth>} />
          <Route path="/terms"                  element={<TermsPage />} />
          <Route path="/privacy"                element={<PrivacyPage />} />
          <Route path="/health-consent"         element={<HealthConsentPage />} />
          {/* 기관 담당자 (일경험 제공기관) 흐름 */}
          <Route
            path="/agency/participant"
            element={
              <RequireAuth userTypes={['MANAGER']}>
                <AgencyParticipantList />
              </RequireAuth>
            }
          />
          {/* 지원기관 이용권관리 — 입금 신고 · 회원 배정 · 사용 내역 (2026-10-06) */}
          <Route
            path="/agency/voucher"
            element={
              <RequireAuth userTypes={['MANAGER']}>
                <AgencyVoucher />
              </RequireAuth>
            }
          />
          {/* 지원기관 나의 페이지 — 검사 이력 · 기간별 · 주요 지표 보고서 (2026-10-23) */}
          <Route
            path="/agency/mypage"
            element={
              <RequireAuth userTypes={['MANAGER']}>
                <AgencyMyPage />
              </RequireAuth>
            }
          />
          {/* 지원기관 — 참여자 개인별 보기: 참여자 1명의 나의 페이지 (나의 페이지 4번 · 이용자 나의 페이지와 같은 화면) */}
          <Route
            path="/agency/report/member/:memberId"
            element={
              <RequireAuth userTypes={['MANAGER']}>
                <AgencyMemberMyPage />
              </RequireAuth>
            }
          />
          {/* 지원기관 — 참여자 개인별 결과지 (나의 페이지 4번) */}
          <Route
            path="/agency/report/:submissionId"
            element={
              <RequireAuth userTypes={['MANAGER']}>
                <AgencyMemberReport />
              </RequireAuth>
            }
          />
        </Route>
        <Route path="/login"                  element={<LoginPage />} />
        <Route path="/intro"                  element={<Navigate to="/" replace />} />
        <Route path="/signup"                 element={<SignupPage />} />
        {/* /start 는 구 QuickStartPage 잔재. 외부 북마크 대비 /signup 리다이렉트. */}
        <Route path="/start"                  element={<Navigate to="/signup" replace />} />
        <Route path="/quick-test"             element={<QuickTestPage />} />
        <Route path="/round/:id/info"         element={<RequireAuth><SubmissionInfo /></RequireAuth>} />
        <Route path="/round/:id/respond"      element={<RequireAuth><SurveyRespond /></RequireAuth>} />
        <Route path="/round/:id/eval-sheet"   element={<EvalSheetToResult />} />
        <Route path="/round/:id/answer-sheet" element={<RequireAuth><MemberAnswerSheet /></RequireAuth>} />
        <Route path="/result/pdf"             element={<RequireAuth><ResultPdfExport /></RequireAuth>} />
        <Route
          path="/agency/participant/:participantId/respond"
          element={
            <RequireAuth userTypes={['MANAGER']}>
              <AgencyJaRespond />
            </RequireAuth>
          }
        />

        {/* 평가자 흐름 */}
        <Route path="/rater/login" element={<RaterLogin />} />
        <Route
          path="/rater"
          element={
            <RequireAuth userTypes={['RATER']}>
              <RaterAssignments />
            </RequireAuth>
          }
        />
        <Route
          path="/rater/assessment/:submissionId"
          element={
            <RequireAuth userTypes={['RATER']}>
              <RaterAssessment />
            </RequireAuth>
          }
        />
        <Route
          path="/rater/assessment/:submissionId/eval-sheet"
          element={
            <RequireAuth userTypes={['RATER']}>
              <RaterEvalSheet />
            </RequireAuth>
          }
        />

        {/* 법적 문서 — 회원가입 동의 화면 및 푸터에서 링크 */}

        {/* dev-feedback (모바일 뷰) — payrx thirdparty 모듈. local/dev 만 노출. */}
        {DEV_TOOLS_ENABLED && (
          <Route path="/dev-feedback/new" element={<FeedbackChatPage appCode="navido-service" />} />
        )}

        <Route path="*"                       element={<Navigate to="/" replace />} />
      </Routes>
      <AlertDialogHost />
      {/* 왼쪽 아래 버전·날짜 배지 제거 (2026-11-01 사용자 지시 mid=490) */}
    </Suspense>
  );
}
